import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, FormControlLabel, MenuItem,
    Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography
} from '@mui/material';
import { APIURL } from '../../../config';
import SubSection from '../Components/SubSection';

const frequencies = ['Daily', 'Weekly', 'Monthly', 'Yearly'];
const employeeName = employee => [employee.emp_first_name, employee.emp_last_name].filter(Boolean).join(' ') || `Employee #${employee.employee_id || employee.emp_id}`;
const errorMessage = error => error.response?.data?.message || 'Unable to load or save compliance settings. Please try again.';

export default function Compliance({ comp_id, user_id }) {
    const [data, setData] = useState({ contacts: [], employees: [], requirements: [] });
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedReport, setSelectedReport] = useState('');
    const [newFrequency, setNewFrequency] = useState('Monthly');
    const [drafts, setDrafts] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [feedback, setFeedback] = useState(null);
    const [saving, setSaving] = useState(false);
    const [reload, setReload] = useState(0);
    const currentContact = data.contacts.find(contact => contact.status === 'ACTIVE');
    const availableReports = (data.reports || []).filter(report => !data.requirements.some(requirement => Number(requirement.feature_id) === Number(report.report_id)));

    const requestConfig = useCallback(() => ({ params: { comp_id, emp_id: user_id } }), [comp_id, user_id]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError('');
        setFeedback(null);
        setSelectedEmployee(null);
        setSelectedReport('');
        setNewFrequency('Monthly');
        setData({ contacts: [], employees: [], requirements: [] });
        axios.get(`${APIURL}/company-compliance`, requestConfig()).then(({ data: result }) => {
            if (cancelled) return;
            setData(result);
            const contact = result.contacts.find(item => item.status === 'ACTIVE');
            setSelectedEmployee(result.employees.find(item => item.emp_id === contact?.employee_id) || null);
            setDrafts(Object.fromEntries(result.requirements.map(item => [item.comp_compliance_req_id, item.frequency])));
        }).catch(error => {
            if (!cancelled) setLoadError(errorMessage(error));
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [requestConfig, reload]);

    const saveContact = async (employeeId, successText) => {
        setSaving(true);
        setFeedback(null);
        try {
            await axios.put(`${APIURL}/company-compliance/contact`, { comp_id, empId: user_id, employee_id: employeeId });
            const { data: result } = await axios.get(`${APIURL}/company-compliance`, requestConfig());
            setData(result);
            const contact = result.contacts.find(item => item.status === 'ACTIVE');
            setSelectedEmployee(result.employees.find(item => item.emp_id === contact?.employee_id) || null);
            setFeedback({ severity: 'success', text: successText || (employeeId === null ? 'Compliance contact deactivated.' : 'Compliance contact saved.') });
        } catch (error) {
            setFeedback({ severity: 'error', text: errorMessage(error) });
        } finally { setSaving(false); }
    };

    const createRequirement = async event => {
        event.preventDefault();
        if (!selectedReport || saving) return;
        setSaving(true);
        setFeedback(null);
        try {
            const { data: requirement } = await axios.post(`${APIURL}/company-compliance/requirements`, {
                comp_id, empId: user_id, feature_id: Number(selectedReport), frequency: newFrequency
            });
            setData(previous => ({ ...previous, requirements: [...previous.requirements, requirement] }));
            setDrafts(previous => ({ ...previous, [requirement.comp_compliance_req_id]: requirement.frequency }));
            setSelectedReport('');
            setFeedback({ severity: 'success', text: 'Compliance requirement added.' });
        } catch (error) {
            setFeedback({ severity: 'error', text: errorMessage(error) });
        } finally { setSaving(false); }
    };

    const saveFrequency = async requirement => {
        setSaving(true);
        setFeedback(null);
        const id = requirement.comp_compliance_req_id;
        const frequency = drafts[id];
        try {
            await axios.put(`${APIURL}/company-compliance/requirements/${id}`, { comp_id, empId: user_id, frequency });
            setData(previous => ({ ...previous, requirements: previous.requirements.map(item => item.comp_compliance_req_id === id ? { ...item, frequency } : item) }));
            setFeedback({ severity: 'success', text: 'Compliance frequency saved.' });
        } catch (error) {
            setFeedback({ severity: 'error', text: errorMessage(error) });
        } finally { setSaving(false); }
    };

    const removeRequirement = async id => {
        setSaving(true);
        setFeedback(null);
        try {
            await axios.delete(`${APIURL}/company-compliance/requirements/${id}`, requestConfig());
            setData(previous => ({ ...previous, requirements: previous.requirements.filter(item => item.comp_compliance_req_id !== id) }));
            setDrafts(previous => {
                const next = { ...previous };
                delete next[id];
                return next;
            });
            setFeedback({ severity: 'success', text: 'Compliance requirement removed.' });
        } catch (error) {
            setFeedback({ severity: 'error', text: errorMessage(error) });
        } finally { setSaving(false); }
    };

    const removeContact = async employeeId => {
        setSaving(true);
        setFeedback(null);
        try {
            await axios.delete(`${APIURL}/company-compliance/contacts/${employeeId}`, requestConfig());
            setData(previous => ({ ...previous, contacts: previous.contacts.filter(contact => contact.employee_id !== employeeId) }));
            if (selectedEmployee?.emp_id === employeeId) setSelectedEmployee(null);
            setFeedback({ severity: 'success', text: 'Employee removed from the compliance list.' });
        } catch (error) {
            setFeedback({ severity: 'error', text: errorMessage(error) });
        } finally { setSaving(false); }
    };

    if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress aria-label="Loading compliance settings" /></Box>;
    if (loadError) return <Alert severity="error" action={<Button color="inherit" onClick={() => setReload(value => value + 1)}>Retry</Button>}>{loadError}</Alert>;

    return (
        <Stack spacing={4}>
            {feedback && <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>{feedback.text}</Alert>}
            <SubSection>
                <Stack spacing={2}>
                    <Typography variant="h5">Compliance Contact</Typography>
                    <Typography color="text.secondary">Assign an active employee to oversee agency compliance. Each employee has one entry. Reassigning an employee activates their existing entry.</Typography>
                    <Typography>Current contact: {currentContact ? employeeName(currentContact) : 'Not assigned'}</Typography>
                    {currentContact && !data.employees.some(item => item.emp_id === currentContact.employee_id) && (
                        <Alert severity="warning">The current contact is no longer an active employee of this agency. Select a replacement or deactivate the assignment.</Alert>
                    )}
                    <Autocomplete
                        options={data.employees}
                        value={selectedEmployee}
                        onChange={(_, employee) => setSelectedEmployee(employee)}
                        getOptionLabel={employee => `${employeeName(employee)} (#${employee.emp_id})`}
                        isOptionEqualToValue={(option, value) => option.emp_id === value.emp_id}
                        disabled={saving}
                        noOptionsText="No active employees available"
                        renderInput={params => <TextField {...params} label="Compliance contact" helperText="Only active employees from this agency are available." />}
                        sx={{ maxWidth: 560 }}
                    />
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" disabled={saving || !selectedEmployee || selectedEmployee.emp_id === currentContact?.employee_id} onClick={() => saveContact(selectedEmployee.emp_id)}>Save contact</Button>
                    </Stack>
                    {data.contacts.length > 0 && <TableContainer>
                        <Table size="small" aria-label="Compliance contact list">
                            <TableHead><TableRow><TableCell>Contact</TableCell><TableCell>Status</TableCell><TableCell>Added on (UTC)</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                            <TableBody>{data.contacts.map(contact => <TableRow key={contact.company_compliance_id}>
                                <TableCell>{employeeName(contact)}</TableCell>
                                <TableCell><Chip size="small" label={contact.status} color={contact.status === 'ACTIVE' ? 'success' : 'default'} /></TableCell>
                                <TableCell>{contact.added_on ? new Date(contact.added_on).toLocaleString(undefined, { timeZone: 'UTC' }) : '—'}</TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                        <FormControlLabel
                                            control={<Switch
                                                size="small"
                                                checked={contact.status === 'ACTIVE'}
                                                disabled={saving || (contact.status !== 'ACTIVE' && !data.employees.some(employee => employee.emp_id === contact.employee_id))}
                                                onChange={(_, active) => saveContact(active ? contact.employee_id : null, active ? 'Compliance contact activated.' : 'Compliance contact deactivated.')}
                                                slotProps={{ input: { role: 'switch', 'aria-label': `Active compliance contact for ${employeeName(contact)}` } }}
                                            />}
                                            label={contact.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                                        />
                                        <Button color="error" disabled={saving} aria-label={`Remove ${employeeName(contact)} from compliance list`} onClick={() => removeContact(contact.employee_id)}>Remove</Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>)}</TableBody>
                        </Table>
                    </TableContainer>}
                </Stack>
            </SubSection>
            <SubSection>
                <Stack spacing={2}>
                    <Typography variant="h5">Compliance Requirements</Typography>
                    <Typography color="text.secondary">Add a compliance report and set how often your agency must complete it.</Typography>
                    <Stack component="form" onSubmit={createRequirement} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                        <TextField select label="Compliance report" value={selectedReport} onChange={event => setSelectedReport(event.target.value)} disabled={saving || availableReports.length === 0} sx={{ minWidth: 260 }}>
                            {availableReports.map(report => <MenuItem key={report.report_id} value={report.report_id}>{`${report.report_name || `Report #${report.report_id}`} (#${report.report_id})`}</MenuItem>)}
                        </TextField>
                        <TextField select label="Frequency" value={newFrequency} onChange={event => setNewFrequency(event.target.value)} disabled={saving} sx={{ minWidth: 140 }}>
                            {frequencies.map(frequency => <MenuItem key={frequency} value={frequency}>{frequency}</MenuItem>)}
                        </TextField>
                        <Button type="submit" variant="contained" disabled={saving || !availableReports.some(report => Number(report.report_id) === Number(selectedReport))}>Add requirement</Button>
                    </Stack>
                    {availableReports.length === 0 && <Alert severity="info">{(data.reports || []).length ? 'All available compliance reports have been added.' : 'No compliance reports are available to add.'}</Alert>}
                    {data.requirements.length === 0 ? <Alert severity="info">No compliance requirements have been configured for this agency.</Alert> : <TableContainer>
                        <Table aria-label="Compliance requirements">
                            <TableHead><TableRow><TableCell>Report</TableCell><TableCell>Frequency</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                            <TableBody>{data.requirements.map(requirement => <TableRow key={requirement.comp_compliance_req_id}>
                                <TableCell>{requirement.report_name || `Report #${requirement.feature_id}`}<Typography variant="caption" display="block" color="text.secondary">Feature ID: {requirement.feature_id}</Typography></TableCell>
                                <TableCell><TextField select size="small" value={drafts[requirement.comp_compliance_req_id] || requirement.frequency} disabled={saving} inputProps={{ 'aria-label': `Frequency for report ${requirement.feature_id}` }} onChange={event => setDrafts(previous => ({ ...previous, [requirement.comp_compliance_req_id]: event.target.value }))} sx={{ minWidth: 140 }}>
                                    {frequencies.map(frequency => <MenuItem key={frequency} value={frequency}>{frequency}</MenuItem>)}
                                </TextField></TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button disabled={saving || drafts[requirement.comp_compliance_req_id] === requirement.frequency} onClick={() => saveFrequency(requirement)}>Save</Button>
                                        <Button color="error" disabled={saving} aria-label={`Remove compliance requirement for report ${requirement.feature_id}`} onClick={() => removeRequirement(requirement.comp_compliance_req_id)}>Remove</Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>)}</TableBody>
                        </Table>
                    </TableContainer>}
                </Stack>
            </SubSection>
        </Stack>
    );
}
